import type { User as FirebaseUser } from 'firebase/auth';
import {
  EmailAuthProvider,
  createUserWithEmailAndPassword,
  reauthenticateWithCredential,
  sendEmailVerification,
  sendPasswordResetEmail,
  signInWithEmailAndPassword,
  signOut,
  updatePassword,
  updateProfile,
} from 'firebase/auth';
import { FirebaseError } from 'firebase/app';
import { httpsCallable } from 'firebase/functions';

import { auth, functions } from './firebase';
import { captureException } from './sentry';
import type {
  CreateUserResponse,
  NewUserRecord,
  SelfSignupNewUserRecord,
} from '../types';

/**
 * User creation error codes shown by signup forms.
 */
export const CreateUserErrorActionCode = {
  WeakPassword: 'weak-password',
  AccountAlreadyExists: 'account-already-exists',
  LoginRequired: 'login-required',
} as const;

/**
 * User creation error code union.
 */
export type CreateUserErrorActionCode =
  typeof CreateUserErrorActionCode[keyof typeof CreateUserErrorActionCode];

/**
 * Error raised for expected account creation failures.
 */
export class CreateUserError extends Error {
  /** Machine-readable error code. */
  public readonly code: CreateUserErrorActionCode;

  public constructor(code: CreateUserErrorActionCode, message: string) {
    super(message);
    this.name = 'CreateUserError';
    this.code = code;
  }
}

/**
 * Logs in with Firebase email/password auth.
 */
export async function logIn(email: string, password: string): Promise<FirebaseUser> {
  const response = await signInWithEmailAndPassword(auth, email, password);
  return response.user;
}

/**
 * Logs out the current Firebase user.
 */
export async function logOut(): Promise<void> {
  await signOut(auth);
}

/**
 * Sends a password reset email.
 */
export async function resetPassword(email: string): Promise<void> {
  try {
    await sendPasswordResetEmail(auth, email);
  } catch (error) {
    if (error instanceof FirebaseError && error.code === 'auth/user-not-found') {
      return;
    }
    throw error;
  }
}

/**
 * Reauthenticates the current user and changes their password.
 */
export async function changePassword(oldPassword: string, newPassword: string): Promise<void> {
  const user = auth.currentUser;
  if (!user?.email) {
    throw new Error('A current email/password user is required.');
  }

  const credential = EmailAuthProvider.credential(user.email, oldPassword);
  await reauthenticateWithCredential(user, credential);
  await updatePassword(user, newPassword);
}

/**
 * Sends a Firebase email verification message to the current user.
 */
export async function sendVerifyEmail(): Promise<void> {
  if (auth.currentUser) {
    await sendEmailVerification(auth.currentUser);
    return;
  }

  throw new CreateUserError(
    CreateUserErrorActionCode.LoginRequired,
    'You must login before you can verify your email address'
  );
}

const createFirebaseUser = async (user: NewUserRecord, password: string): Promise<string> => {
  try {
    const fbUser = await createUserWithEmailAndPassword(auth, user.email, password);
    await updateProfile(fbUser.user, {
      displayName: `${user.firstname} ${user.lastname}`,
    });
    return fbUser.user.uid;
  } catch (error) {
    if (error instanceof FirebaseError) {
      switch (error.code) {
      case 'auth/weak-password':
        throw new CreateUserError(
          CreateUserErrorActionCode.WeakPassword,
          'The password you selected is too weak, try again'
        );

      case 'auth/credential-already-in-use':
      case 'auth/email-already-in-use':
      case 'auth/account-exists-with-different-credential':
        throw new CreateUserError(
          CreateUserErrorActionCode.AccountAlreadyExists,
          'That email address is already in use'
        );

      default:
        throw error;
      }
    }

    throw error;
  }
};

/**
 * Calls the MeshOS user provisioning Firebase function.
 */
export async function createMeshUser(user: SelfSignupNewUserRecord): Promise<CreateUserResponse> {
  if (!auth.currentUser) {
    throw new CreateUserError(
      CreateUserErrorActionCode.LoginRequired,
      'You must login before you can finish creating your account'
    );
  }

  const userSignUp = httpsCallable<SelfSignupNewUserRecord, CreateUserResponse>(functions, 'userSignUp');
  const response = await userSignUp(user);
  return response.data;
}

/**
 * Creates a Firebase auth user, provisions the MeshOS profile, and sends verification.
 */
export async function createUser(user: NewUserRecord, password: string): Promise<CreateUserResponse> {
  const firebaseUid = await createFirebaseUser(user, password);
  const response = await createMeshUser({
    ...user,
    firebaseUid,
  });

  try {
    await sendVerifyEmail();
  } catch (error) {
    captureException(error);
    console.error('unable to send verification email:', error);
  }

  return response;
}
